/**
 * TaskPool — Sequential async task queue.
 *
 * Enqueues async tasks and processes them one at a time.
 * If a task is added while another is running, it waits in the queue.
 * Inspired by UDG Poker's TaskPool pattern.
 *
 * Liveness contract (learned from the cee-lo stuck-in-betting incident):
 * a task whose promise NEVER settles (e.g. an awaited GSAP tween killed
 * without resolving its awaiter) must not jam the queue forever — every
 * later game:update would silently pile up behind it and the client would
 * freeze on the last-rendered phase. Two defenses:
 *   1. Watchdog: a task still pending after TASK_TIMEOUT_MS is abandoned
 *      (loud console.error) and the queue keeps draining.
 *   2. reset(): a hard boundary (resume / new game session) that drops the
 *      queue AND orphans the in-flight task — its eventual settle is
 *      ignored via the generation counter, and a fresh flush can start
 *      immediately.
 *
 * IMPORTANT: neither defense CANCELS the abandoned task's continuation —
 * JS has no preemption. If its suspended await is ever released later, the
 * corpse resumes running side effects over the new owner's state. That's
 * what `onAbandon` is for: the owner bumps its cooperative-cancellation
 * epoch there, and long-running choreography primitives check that epoch
 * after every await and park instead of continuing.
 */

export type Task = () => Promise<void>;

interface QueuedTask {
  task: Task;
  label: string;
}

export class TaskPool {
  /** A legit task is an onExit+onEnter choreography chain (camera lerps,
   *  flyouts, shooter handoffs) — a few seconds at most. The server's own
   *  ready-ack gate gives up at 8s, so by 15s the task is provably wedged. */
  private static readonly TASK_TIMEOUT_MS = 15000;

  private queue: QueuedTask[] = [];
  private running = false;
  /** Bumped by reset(); a flush loop (or armed watchdog) that observes a
   *  different generation than it started with belongs to a dead session
   *  and exits/skips silently. */
  private generation = 0;
  /** Notified whenever a task is abandoned — by the watchdog or by reset()
   *  while a task was in flight. The owner uses this to bump its
   *  cooperative-cancellation epoch so the abandoned continuation parks at
   *  its next await instead of replaying stale choreography. */
  private readonly onAbandon: ((label: string) => void) | null;

  constructor(opts: { onAbandon?: (label: string) => void } = {}) {
    this.onAbandon = opts.onAbandon ?? null;
  }

  /** Enqueue a task. Starts processing immediately if idle. */
  enqueue(task: Task, label = 'unlabeled'): void {
    this.queue.push({ task, label });
    if (!this.running) {
      this.flush();
    }
  }

  /** Process queue sequentially. */
  private async flush(): Promise<void> {
    const gen = this.generation;
    this.running = true;
    while (this.queue.length > 0) {
      const item = this.queue.shift()!;
      this.inflightLabel = item.label;
      try {
        await this.runWithWatchdog(item, gen);
      } catch (e) {
        console.error('[TaskPool] Task failed:', item.label, e);
      }
      // reset() superseded this loop while we were awaiting — a newer flush
      // owns the queue (and `running`) now. Exit without touching either.
      if (gen !== this.generation) return;
      this.inflightLabel = null;
    }
    this.running = false;
  }

  /** Label of the task currently in flight (null when idle). */
  private inflightLabel: string | null = null;

  /** Await the task, but never longer than TASK_TIMEOUT_MS. On timeout the
   *  task is abandoned (onAbandon fires so its continuation gets parked at
   *  its next await) and the queue stays live. */
  private runWithWatchdog(item: QueuedTask, gen: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        // A reset() happened while this watchdog was armed — the whole
        // generation is already abandoned; don't fire a stale false alarm
        // into the new session's console.
        if (gen !== this.generation) {
          settled = true;
          resolve();
          return;
        }
        settled = true;
        console.error(
          `[TaskPool] Task '${item.label}' still pending after ${TaskPool.TASK_TIMEOUT_MS}ms — ` +
          `abandoning it to keep the update queue alive (${this.queue.length} queued behind it)`
        );
        this.onAbandon?.(item.label);
        resolve();
      }, TaskPool.TASK_TIMEOUT_MS);

      let taskPromise: Promise<void>;
      try {
        taskPromise = item.task();
      } catch (err) {
        // Synchronous throw — settle immediately so the armed watchdog
        // can't later log a false "still pending" for a task that already
        // failed on the spot.
        settled = true;
        clearTimeout(timer);
        reject(err);
        return;
      }
      taskPromise.then(
        () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve();
        },
        (err) => {
          if (settled) {
            // Abandoned earlier (watchdog/reset), now failing late — keep
            // the fingerprint, this is exactly the kind of corpse crash we
            // want visible in a prod repro.
            console.error('[TaskPool] Abandoned task later failed:', item.label, err);
            return;
          }
          settled = true;
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  /**
   * Hard reset at a session/resume boundary: drops queued tasks AND abandons
   * the in-flight one. The old flush loop (possibly suspended forever on a
   * dead await) is orphaned via the generation bump; `running` is cleared so
   * the next enqueue starts a fresh flush immediately. This is what lets a
   * background→resume state:full snap past a wedged animation await instead
   * of queueing behind it.
   */
  reset(): void {
    this.queue.length = 0;
    this.generation++;
    if (this.running && this.inflightLabel !== null) {
      // The in-flight task is now a corpse — let the owner park its
      // continuation (cooperative cancellation) so a later tween-release
      // can't revive it over the new session's state.
      this.onAbandon?.(this.inflightLabel);
      this.inflightLabel = null;
    }
    this.running = false;
  }

  get pending(): number {
    return this.queue.length;
  }

  get isRunning(): boolean {
    return this.running;
  }
}
